import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Box, Typography, CircularProgress, Button, Chip } from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import ReadOnlyTierList from "../Components/Share/ReadOnlyTierList";
import SeoHead from "../Components/SEO/SeoHead";
import { BASE_API_URL, BASE_SITE_URL } from "../config";

const SharePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    fetch(`${BASE_API_URL}/api/share/${id}`)
      .then(res => {
        if (!res.ok) throw new Error("Not found");
        return res.json();
      })
      .then(setData)
      .catch(() => setError(t("share.notFound")))
      .finally(() => setLoading(false));
  }, [id]);

  const handleCopyLink = () => {
    const url = `${BASE_SITE_URL}/share/${id}`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success(t("toast.linkCopied"));
    }).catch(() => {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      toast.success(t("toast.linkCopied"));
    });
  };

  if (loading) {
    return (
      <>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100vh", gap: 2 }}>
          <Typography variant="h5" color="var(--text-primary)">{t("share.notFound")}</Typography>
          <Button component={RouterLink} to="/" variant="outlined">{t("share.goHome")}</Button>
        </Box>
      </>
    );
  }

  const displayName = data.alias_name || data.user_name?.split(" ")[0] || t("gallery.anonymous");
  const title = data.title || data.type || "Tier List";
  const customTiers = data.custom_tiers
    ? (typeof data.custom_tiers === "string" ? JSON.parse(data.custom_tiers) : data.custom_tiers)
    : null;

  return (
    <>
      <SeoHead
        title={`${title} - by ${displayName}`}
        description={data.description || `Check out ${displayName}'s ${data.list_type || "tier list"} ranking on Music Tier List`}
        path={`/share/${id}`}
        imageUrl={`${BASE_SITE_URL}/api/gallery/${id}/image`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "CreativeWork",
          "name": title,
          "author": { "@type": "Person", "name": displayName },
          "url": `${BASE_SITE_URL}/share/${id}`,
          "image": `${BASE_SITE_URL}/api/gallery/${id}/image`,
          ...(data.description ? { "description": data.description } : {}),
          "isPartOf": {
            "@type": "WebSite",
            "name": "Music Tier List",
            "url": BASE_SITE_URL,
          },
        }}
      />
      <Box sx={{ minHeight: "100vh", bgcolor: "var(--page-bg)", color: "var(--text-primary)" }}>
        {/* Header bar */}
        <Box sx={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          px: 3, py: 2, borderBottom: "1px solid var(--border-soft)",
          flexWrap: "wrap", gap: 1,
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t("gallery.by")}{" "}
                <RouterLink
                  to={`/profile/${data.alias_name || data.user_id}`}
                  style={{ color: "inherit", textDecoration: "underline" }}
                >
                  {displayName}
                </RouterLink>
              </Typography>
              {data.list_type && (
                <Chip
                  label={data.group_by && data.group_by !== "All" ? `${data.list_type} - ${data.group_by}` : data.list_type}
                  size="small"
                  sx={{ fontSize: "0.7rem", height: 20 }}
                />
              )}
            </Box>
            {data.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {data.description}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <ThumbUpOutlinedIcon fontSize="small" sx={{ opacity: 0.6 }} />
              <Typography variant="body2">{data.like_count || 0}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <VisibilityIcon fontSize="small" sx={{ opacity: 0.6 }} />
              <Typography variant="body2">{data.view_count || 0}</Typography>
            </Box>
            <Button
              onClick={handleCopyLink}
              variant="outlined"
              size="small"
              startIcon={<ShareIcon />}
              sx={{
                textTransform: "none",
                borderColor: "var(--border-mid)",
                color: "var(--text-secondary)",
                "&:hover": { borderColor: "var(--border-strong)", backgroundColor: "var(--hover-overlay)" },
              }}
            >
              {t("share.share")}
            </Button>
          </Box>
        </Box>

        {/* Tier list content */}
        {data.hasTierData && data.tier_data ? (
          <ReadOnlyTierList
            title=""
            tierNames={data.tier_data.tierNames}
            tiers={data.tier_data.tiers}
            customTiers={customTiers}
            listType={data.list_type || "Albums"}
          />
        ) : (
          <Box sx={{ textAlign: "center", p: 3 }}>
            {data.thumbnail ? (
              <Box
                component="img"
                src={data.thumbnail}
                alt={title}
                sx={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain", borderRadius: 1 }}
              />
            ) : (
              <Typography color="text.secondary">
                {t("share.legacyMessage")}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export default SharePage;
